import { Fragment, useEffect, useState, type ReactNode } from "react";
import { VavrNews } from "../support/News.tsx";
import { ActionTile, Avatar, Banner, Button, EmptyState, Icon, Timeline, TimelineItem, type IconName } from "@vavr/ui";
import { Link, navigate } from "../shared/router.tsx";
import { useBlobUrl } from "../shared/useBlobUrl.ts";
import { api } from "../shared/api.ts";
import { passkeysSupported } from "../shared/webauthn.ts";
import { day, eventLabel, fullName } from "../shared/format.ts";
import { G, useGuardian } from "./context.tsx";
import { allowsAppRequest, dayTrack, sentence, todayStatus, type GuardianChild, type GuardianSite, type Overview, type TodayTone } from "./status.ts";
import { slot } from "../shared/slots.tsx";

const blobPath = (site: string, sha: string) => `/api/v1/guardian/sites/${site}/blobs/${sha}`;

/** The icon for an event in a timeline: arriving, leaving, absent, moved, corrected. */
export const EVENT_ICON: Record<string, IconName> = { pickup: "leave", dropoff: "arrive", absence: "calendar", transfer: "door", correction: "note" };
export const EVENT_TONE: Record<string, "success" | "info" | "warning" | "neutral"> = { pickup: "success", dropoff: "info", absence: "warning" };
const RING: Record<TodayTone, "neutral" | "success" | "info" | "warning"> = { neutral: "neutral", success: "success", info: "info", warning: "warning" };

function greeting(now: number) {
  const h = new Date(now).getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}
const joinNames = (n: string[]) => (n.length <= 2 ? n.join(" and ") : `${n.slice(0, -1).join(", ")} and ${n.at(-1)}`);

function ChildAvatar({ site, child, ring }: { site: string; child: GuardianChild; ring: TodayTone }) {
  const src = useBlobUrl(child.photo_sha256 ? blobPath(site, child.photo_sha256) : null);
  return <Avatar kind="child" size="lg" name={fullName(child)} src={src ?? undefined} ring={RING[ring]} />;
}

export interface HomeViewProps {
  overview: Overview;
  now?: number;
  /** Extra cards (push and passkey offers) rendered under the actions. */
  offers?: ReactNode;
}

/**
 * Home (redesign 2026-09-27): a greeting, each child's day as a four-step track (dropped off,
 * in class, called, picked up), four quick actions, and the latest few events. The pass is not
 * here: it is the button pinned above the tabs on every screen.
 */
export function HomeView({ overview, now = Date.now(), offers }: HomeViewProps) {
  if (!overview.sites.length) {
    return (
      <div className="pmu-g-home">
        <EmptyState icon="child" title="No children linked yet" description="When your child's school or center adds you as a guardian, their pickups show here." />
      </div>
    );
  }
  const kids = overview.sites.flatMap((s) => s.children.map((c) => c.first));
  const places = overview.sites.map((s) => s.site.name).join(" · ");
  // Where the school allows it, the parent can also ask without scanning (Handoff's "without scanning").
  const tileHint = (kind: "pickup" | "dropoff") => (overview.sites.some((s) => s.children.length && allowsAppRequest(s, kind)) ? "Scan the code or ask from here" : "Scan the door code");
  const recent = overview.sites.flatMap((s) => s.recent.map((e) => ({ e, s }))).sort((a, b) => b.e.server_ts - a.e.server_ts).slice(0, 3);
  return (
    <div className="pmu-g-home pmg-home">
      <header className="pmg-greet">
        <h1 className="pmg-greet__title">{greeting(now)}</h1>
        <p className="pmg-greet__sub">{kids.length ? `${joinNames(kids)} · ${places}` : places}</p>
      </header>
      {/* Emergency reunification at a school: where to collect the children, before anything else. */}
      {overview.sites.filter((s) => s.site.reunification).map((s) => (
        <Banner key={`re-${s.site.id}`} tone="danger" title={`Emergency at ${s.site.name}: collect your children at ${s.site.reunification!.place ?? "the meeting point"}`}>
          Staff hand each child only to an approved adult there. Bring ID.
        </Banner>
      ))}
      {offers}
      {overview.sites.flatMap((s) => s.children.map((c) => <ChildDay key={`${s.site.id}:${c.id}`} s={s} c={c} now={now} />))}
      {kids.length > 0 && (
        <nav className="pmg-quick" aria-label="Quick actions">
          <ActionTile icon="leave" title="Pick up" description={tileHint("pickup")} onClick={() => navigate(`${G}/handoff/pickup`)} />
          <ActionTile icon="arrive" title="Drop off" description={tileHint("dropoff")} onClick={() => navigate(`${G}/handoff/dropoff`)} />
          <ActionTile icon="note" title="Tell the school" description="Absent or leaving early" onClick={() => navigate(`${G}/requests`)} />
          <ActionTile icon="user" title="One-time code" description="Someone else collects" onClick={() => navigate(`${G}/delegate`)} />
        </nav>
      )}
      {/* Cards from modules (e.g. today's bus), after the children and before the history (PMU-181). */}
      {slot("guardian.home").map((c) => <Fragment key={c.id}>{c.render()}</Fragment>)}
      <section aria-labelledby="pmg-recent">
        <h2 id="pmg-recent" className="pmu-g-section">Latest<Link className="pmu-g-section__meta pmg-seeall" href={`${G}/activity`}>See all</Link></h2>
        <div className="pmu-g-panel">
          {recent.length === 0 ? <p className="pmu-muted pmu-g-panel__empty">Nothing recorded yet. Drop-offs and pickups show here.</p> : (
            <Timeline label="Latest activity">
              {recent.map(({ e, s }) => {
                const first = s.children.find((c) => c.id === e.child_id)?.first ?? "Your child";
                return (
                  <TimelineItem key={e.id} icon={EVENT_ICON[e.type] ?? "note"} tone={EVENT_TONE[e.type] ?? "neutral"} title={sentence(e, first, s.site.tz)}
                    href={`${G}/sites/${s.site.id}/events/${e.id}`} onClick={(ev) => { ev.preventDefault(); navigate(`${G}/sites/${s.site.id}/events/${e.id}`); }}>
                    {`${eventLabel(e.type, "Update")} · ${day(e.server_ts, s.site.tz)}`}
                  </TimelineItem>
                );
              })}
            </Timeline>
          )}
        </div>
      </section>
    </div>
  );
}

/** One child's day: photo, where they are now, and the four-step track. */
function ChildDay({ s, c, now }: { s: GuardianSite; c: GuardianChild; now: number }) {
  const t = dayTrack(c, s.recent, s.site.tz, now);
  const st = todayStatus(c, s.recent, s.site.tz, now);
  return (
    <article className={`pmg-day ${t.absent ? "pmg-day--absent" : ""}`} aria-label={`${fullName(c)}: ${t.title}`}>
      <div className="pmg-day__top">
        <ChildAvatar site={s.site.id} child={c} ring={st.tone} />
        <div className="pmg-day__who">
          <h2 className="pmg-day__title">{t.title}</h2>
          <p className="pmg-day__since">{[t.since, s.site.name].filter(Boolean).join(" · ")}</p>
        </div>
      </div>
      {!t.absent && (
        <ol className="pmg-track" aria-label="Today">
          {t.steps.map((x) => (
            <li key={x.label} className={`pmg-track__step pmg-track__step--${x.state}`} aria-current={x.state === "now" ? "step" : undefined}>
              <span>{x.label}</span>{x.at && <time>{x.at}</time>}
            </li>
          ))}
        </ol>
      )}
      <div className="pmg-day__links">
        {st.event && <Link className="pmu-g-chip pmg-day__chip" href={`${G}/sites/${s.site.id}/events/${st.event.id}`}><Icon name="shield" />See the record</Link>}
        <Link className="pmu-g-chip pmg-day__chip" href={`${G}/sites/${s.site.id}/children/${c.id}`}><Icon name="clock" />History</Link>
      </div>
    </article>
  );
}

const DISMISS_KEY = "pmu.guardian.passkey-offer-dismissed";

/** Home with the one-time offers: turn on notifications (apps) and add a passkey (after first sign-in). */
export function Home() {
  const { overview, platform } = useGuardian();
  const [push, setPush] = useState<"granted" | "denied" | "prompt" | null>(null);
  const [passkeyOffer, setPasskeyOffer] = useState(false);
  useEffect(() => { platform.push?.permission().then(setPush, () => setPush(null)); }, [platform]);
  useEffect(() => {
    let dismissed = false;
    try { dismissed = localStorage.getItem(DISMISS_KEY) === "1"; } catch { /* storage blocked */ }
    if (dismissed || !platform.passkeys || !passkeysSupported()) return;
    api("GET", "/api/v1/me").then((me) => setPasskeyOffer(me.passkeys.length === 0), () => undefined);
  }, [platform]);
  const offers = (push === "prompt" || passkeyOffer) && (
    <div className="pmu-g-offers">
      {push === "prompt" && (
        <Banner tone="info" title="Know the moment your child leaves" actions={<Button size="sm" onClick={() => navigate(`${G}/settings`)}>Turn on</Button>}>
          Get a notification for every pickup and drop-off.
        </Banner>
      )}
      {passkeyOffer && (
        <Banner tone="info" title="Sign in faster next time" actions={
          <>
            <Button size="sm" onClick={() => navigate(`${G}/settings`)}>Add a passkey</Button>
            <Button size="sm" variant="ghost" onClick={() => { try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* */ } setPasskeyOffer(false); }}>Not now</Button>
          </>
        }>
          Use your fingerprint, face or phone PIN instead of an email link.
        </Banner>
      )}
    </div>
  );
  return <HomeView overview={overview} offers={<><VavrNews />{offers || null}</>} />;
}
